import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, ApiError, setToken } from '../lib/api';
import { Shield } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email || !password) { setError('Vul alle velden in.'); return; }
    setIsSubmitting(true);
    setError('');
    try {
      const data = await api.post<{ ok: boolean; token?: string; requires_2fa?: boolean; temp_token?: string }>(
        '/auth/login', { email: email.trim().toLowerCase(), password }, { skipAuth: true }
      );

      if (data.requires_2fa && data.temp_token) {
        setNeeds2FA(true);
        setTempToken(data.temp_token);
        setIsSubmitting(false);
        return;
      }

      if (data.token) {
        setToken(data.token);
        await refreshAuth();
        // Fetch user to determine redirect
        const userData = await api.get<{ platformUser: { role: string } }>('/auth/me');
        const targetPath = userData.platformUser?.role === 'super_admin' ? '/admin' : '/dashboard';
        navigate(from || targetPath, { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? 'Onjuist e-mailadres of wachtwoord.' : 'Er is een fout opgetreden.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handle2FASubmit(e: FormEvent) {
    e.preventDefault();
    if (!totpCode) { setError('Vul de code in.'); return; }
    setIsSubmitting(true);
    setError('');
    try {
      const data = await api.post<{ ok: boolean; token: string }>(
        '/auth/2fa/verify', { temp_token: tempToken, totp_code: totpCode }, { skipAuth: true }
      );

      if (data.token) {
        setToken(data.token);
        await refreshAuth();
        const userData = await api.get<{ platformUser: { role: string } }>('/auth/me');
        const targetPath = userData.platformUser?.role === 'super_admin' ? '/admin' : '/dashboard';
        navigate(from || targetPath, { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ongeldige code.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="block text-center text-2xl font-bold text-brand-600 mb-8">Occiput</Link>
        <h1 className="text-center text-2xl font-bold text-gray-900">
          {needs2FA ? 'Tweestapsverificatie' : 'Inloggen'}
        </h1>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card p-8">
          {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          {!needs2FA ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="label">E-mailadres</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input" />
              </div>
              <div>
                <label className="label">Wachtwoord</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="input" />
              </div>
              <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-2.5">
                {isSubmitting ? 'Inloggen...' : 'Inloggen'}
              </button>
            </form>
          ) : (
            <form onSubmit={handle2FASubmit} className="space-y-5">
              <div className="flex items-center gap-3 p-4 bg-brand-50 rounded-lg">
                <Shield className="w-5 h-5 text-brand-600 flex-shrink-0" />
                <p className="text-sm text-gray-700">
                  Voer de 6-cijferige code in van je authenticator app, of gebruik een backup-code.
                </p>
              </div>
              <div>
                <label className="label">Verificatiecode</label>
                <input
                  type="text"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\s/g, ''))}
                  className="input text-center text-lg tracking-widest font-mono"
                  placeholder="000000"
                  maxLength={8}
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>
              <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-2.5">
                {isSubmitting ? 'Verifying...' : 'Verifieren'}
              </button>
              <button
                type="button"
                onClick={() => { setNeeds2FA(false); setTotpCode(''); setError(''); }}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
              >
                Terug naar inloggen
              </button>
            </form>
          )}

          {!needs2FA && (
            <p className="mt-6 text-center text-sm text-gray-600">
              Nog geen account? <Link to="/register" className="text-brand-600 font-medium hover:underline">Aanmelden</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
